import { SKILL_SLOT_MAX } from "@/character/skills/constants";
import {
  normalizeSkillAttackPattern,
  normalizeSkillKind,
  type SkillBarState,
  type SkillDefinition,
} from "@/character/skills/types";
import { normalizeGridSpec } from "@/character/grid";
import { STAT_IDS } from "@/game/model/character-stats";

function normalizeSkillDefinition(raw: unknown): SkillDefinition | null {
  if (!raw || typeof raw !== "object") return null;
  const s = raw as Partial<SkillDefinition>;
  if (typeof s.id !== "string" || typeof s.name !== "string") return null;
  if (typeof s.spritesheetDataUrl !== "string" || !s.spritesheetDataUrl) return null;
  if (!s.grid || typeof s.grid.frameWidth !== "number") return null;
  if (typeof s.iconDataUrl !== "string") return null;
  const buffStatBonus = s.buffStatBonus;
  const normalizedBuff: SkillDefinition["buffStatBonus"] = {};
  if (buffStatBonus && typeof buffStatBonus === "object") {
    for (const id of STAT_IDS) {
      const n = buffStatBonus[id];
      if (typeof n === "number" && n > 0) normalizedBuff[id] = Math.floor(n);
    }
  }
  return {
    id: s.id,
    name: s.name.trim().slice(0, 48) || "สกิล",
    kind: normalizeSkillKind(s.kind),
    durationMs: typeof s.durationMs === "number" ? Math.max(0, s.durationMs) : 5000,
    spritesheetDataUrl: s.spritesheetDataUrl,
    grid: normalizeGridSpec(s.grid),
    iconDataUrl: s.iconDataUrl,
    frameRate: typeof s.frameRate === "number" ? s.frameRate : 8,
    createdAt: typeof s.createdAt === "number" ? s.createdAt : Date.now(),
    sourceSheetDataUrl: typeof s.sourceSheetDataUrl === "string" ? s.sourceSheetDataUrl : undefined,
    cooldownCards: Math.max(0, Math.floor(s.cooldownCards ?? 0)),
    damageMultiplier: typeof s.damageMultiplier === "number" ? s.damageMultiplier : undefined,
    attackPattern: normalizeSkillAttackPattern(s.attackPattern),
    buffStatBonus: Object.keys(normalizedBuff).length > 0 ? normalizedBuff : undefined,
    skillApCards: Math.max(0, Math.floor(s.skillApCards ?? 0)),
    buffDurationCards: Math.max(0, Math.floor(s.buffDurationCards ?? 0)),
    aoeRangeCards: Math.max(0, Math.floor(s.aoeRangeCards ?? 0)),
  };
}

export function normalizeSkillBarFromCloud(raw: unknown): SkillBarState | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Partial<SkillBarState>;
  const library: SkillDefinition[] = [];
  if (Array.isArray(o.library)) {
    for (const entry of o.library) {
      const norm = normalizeSkillDefinition(entry);
      if (norm) library.push(norm);
    }
  }
  const slots = Array.from({ length: SKILL_SLOT_MAX }, (_, i) => {
    const id = Array.isArray(o.slots) ? o.slots[i] : null;
    if (typeof id === "string" && library.some((sk) => sk.id === id)) return id;
    return null;
  });
  const slotAuto = Array.from({ length: SKILL_SLOT_MAX }, (_, i) => {
    return Array.isArray(o.slotAuto) && o.slotAuto[i] === true;
  });
  return {
    version: 3,
    slots,
    library,
    slotAuto,
    masterAuto: o.masterAuto === true,
  };
}
